import { forwardRef, useEffect, useImperativeHandle, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
    getCustomerConsigneeSearchDropdown,
  getFieldValuesByTableAndId,
  getOtherRefSearchDropdown,
  getTextByValueAndKey,
} from "../../../api/keyvalue";
import { FORMS, MODULES } from "../../../const";
import useDynamicValidation from "../../../hooks/useDynamicValidation";
import useFormVisibility from "../../../hooks/useFormVisibility";
import useUserCompany from "../../../hooks/useUserCompany";
import { requestActions } from "../../../store/request-slice";
import Grid from "../../Grid";
import Button from "../../input/Button";
import Dropdown from "../../input/Dropdown";
import Input from "../../input/Input";
import SearchDropdown from "../../input/SearchDropdown";
import LoadingSpinner from "../../LoadingSpinner";
import ButtonRenderer from "../../renderer/ButtonRenderer";
import { ResizableScrollingTextRenderer } from "../../renderer/StyleRenderer";
import {
  requestMultipleJobValidation,
  transporterCreationValidation,
  validateRequestMultipleJob,
  validateRequestTransport,
} from "../../validations/validateRequestCreation";

const FORM = FORMS.REQUEST.TRANSPORT.form;

const RequestTransporter = forwardRef(
  ({ dropdown, editMode, isLoading }, ref) => {
    const dispatch = useDispatch();
    const errors = useSelector((state) => state.request.requestTransporterErrors);
    const userCompany = useUserCompany();
    const [currentErrors, setCurrentErrors] = useState(errors);
    const customerId = useSelector((state) => state.request.reqLCustomerId);
    const plantId = useSelector((state) => state.request.reqLPlantId);
    const auth = useSelector((state) => state.user.permissions[MODULES.Transaction]);
    const multiRequestViewPermission = auth?.[FORM]?.MULTIPLE_REQUEST?.VIEW;
    const multiRequestCreatePermission = auth?.[FORM]?.MULTIPLE_REQUEST?.CREATE;
    const multiRequestEditPermission = auth?.[FORM]?.MULTIPLE_REQUEST?.UPDATE;
    const multiRequestDeletePermission = auth?.[FORM]?.MULTIPLE_REQUEST?.DELETE;

    const isFormVisibilityLoading = useSelector((state) => state.formVisibility.loading);
    const reqConfirmStatus = useSelector((state) => state.request.reqConfirmationStatus);
    const reqCreateType = useSelector((state) => state.request.reqCreationType);
    const currentGridData = useSelector((state) => state.request.requestMultipleJobs);
    const [reqType, setReqType] = useState(1);
    const [confirmationType, setConfirmationType] = useState(1);
    const [multiRequestTransporterErrors, setMultiRequestTransporterErrors] = useState({});
    const [buttonClicked, setButtonClicked] = useState(false);
    const [isAddLoading, setIsAddLoading] = useState(false);
    const [gridData, setGridData] = useState([]);
    const [filteredGridData, setFilteredGridData] = useState([]);
    const [currentItem, setCurrentItem] = useState({});
    const [count, setCount] = useState(1);
    const [isContainerDisabled, setIsContainerDisabled] = useState(false);

    useEffect(() => {
        setFilteredGridData(gridData?.length > 0 ? gridData?.filter((item) => item.rmjActive === 1) : []);
    }, [gridData]);

    useEffect(() => {
        if (editMode) {
            setReqType(reqCreateType);
            setConfirmationType(reqConfirmStatus);
            if (reqCreateType === 2) {
                setGridData(currentGridData);
            }
        }
    }, [editMode, reqCreateType, reqConfirmStatus, currentGridData]);

    useFormVisibility({
        form: FORM, keyValue: customerId ? "stakeholder" : "default", type: customerId ?? 1, useDynamicFields: true,
    });

    const dynamicValidationStatus = useDynamicValidation(transporterCreationValidation, FORM, true);

    useEffect(() => {
        setCurrentErrors(errors);
    }, [errors]);

    const vehicleMaxCbm = useSelector((state) => state.request.vehicleMaxCbm);
    const currentValues = useSelector((state) => state.request.currentValues.requestTransporterData);
    const nextButtonErrors = useSelector((state) => state.error.requestCreationErrors.transportErrors);

    const viewOnlyMode = useSelector((state) => state.request.viewOnlyMode);

    const [requestTransporter, setRequestTransporter] = useState({
        reqItemTypes: 7,
        reqManageItems: 2,
        reqMultiConsignee: 2,
        reqConsignmentType: null,
        reqVehicleTypeId: null,
        reqContainerType: null,
        reqContainerno: "",
        reqNumberOfVehicles: null,
        reqNumberOfTrailers: null,
        reqGrossWeight: null,
        reqTotalValye: null,
        reqConsignee: null,
        reqConsigneeCbm: null,
        reqConsigneeQty: null, // reqConfirmationStatus: 1,
        reqKAGNo: null,
    });

    useEffect(() => {
        setCurrentErrors((prevState) => ({
            ...prevState, ...nextButtonErrors,
        }));
    }, [nextButtonErrors]);

    useEffect(() => {
        if (editMode && currentValues) {
            setRequestTransporter(currentValues.requestTransporter);
            setSingleConsignee(currentValues.singleConsigneeData);
            setMultiConsignmentType(currentValues.multiConsignmentType);
        }
    }, [editMode, currentValues]);

    const [singleConsignee, setSingleConsignee] = useState({
        rcoLocation: null,
        rcoConsignee: null,
        rcoCbm: null,
        rcoCbmUom: null,
        rcoQty: null,
        rcoQtyUom: null,
        rcoActive: 1,
    });

    const [searchFieldReset, setSearchFieldReset] = useState(false);
    const [ctSearchFieldReset, setCtSearchFieldReset] = useState(false);

    const [multiConsignmentType, setMultiConsignmentType] = useState("");

    useEffect(() => {
        (async () => {
            if (requestTransporter.reqVehicleTypeId) {
                const response = await getFieldValuesByTableAndId({
                    tableName: "masterdata.tms_m_vehicle_type",
                    primaryKey: "vht_id",
                    ids: requestTransporter.reqVehicleTypeId.toString(),
                    fieldNames: "vht_max_cbm",
                });
                if (response && response.length > 0) {
                    dispatch(requestActions.setRequest({
                        vehicleMaxCbm: parseFloat(response[0].field1),
                    }));
                }
            }
        })();
    }, [requestTransporter.reqVehicleTypeId, dispatch]);

    const [multiRequestTransporter, setMultiRequestTransporter] = useState({
        rmjRequest: null,
        rmjJob: null,
        rmjCompany: userCompany.comId,
        rmjReferance: null,
        rmjDescription: "",
        rmjVehicleType: null,
        rmjVehicleQty: 1,
        rmjMaxCmb: 0,
        rmjConsignees: null,
        rmjConsigneeName: "",
        rmjConsigneeCbm: "",
        rmjContainerType: null,
        rmjContianerNumber: "",
        rmjCompleted: 1,
        rmjStatus: 2,
        rmjActive: 1,
        vehicleTypeValue: "",
        containerTypeValue: "",
        completedValue: "",
    });

    useEffect(() => {
        (async () => {
            if (multiRequestTransporter.rmjVehicleType) {
                const response = await getFieldValuesByTableAndId({
                    tableName: "masterdata.tms_m_vehicle_type",
                    primaryKey: "vht_id",
                    ids: multiRequestTransporter.rmjVehicleType.toString(),
                    fieldNames: "vht_max_cbm",
                });
                if (response && response.length > 0) {
                    setMultiRequestTransporter((prevState) => ({
                        ...prevState, rmjMaxCmb: parseFloat(response[0].field1),
                    }));
                }
            } else {
                setMultiRequestTransporter((prevState) => ({
                    ...prevState, rmjMaxCmb: 0,
                }));
            }
        })();
    }, [multiRequestTransporter.rmjVehicleType, dispatch]);

      const isFirstCustomerRender = useRef(true);
      useEffect(() => {
          if (isFirstCustomerRender.current) {
              isFirstCustomerRender.current = false;
              return;
          }
          setSingleConsignee((prevState) => ({
              ...prevState,
              rcoConsignee: null,
          }));
          setMultiRequestTransporter((prevState) => ({
              ...prevState,
              rmjConsignees: null,
              rmjConsigneeName: "",
          }));
          setSearchFieldReset(true);
      }, [customerId]);

    useEffect(() => {
        if (buttonClicked) {
            setMultiRequestTransporter({
                rmjRequest: null,
                rmjJob: null,
                rmjCompany: userCompany.comId,
                rmjReferance: null,
                rmjDescription: "",
                rmjVehicleType: null,
                rmjVehicleQty: 1,
                rmjMaxCmb: 0,
                rmjConsignees: null,
                rmjConsigneeName: "",
                rmjConsigneeCbm: "",
                rmjContainerType: null,
                rmjContianerNumber: "",
                rmjCompleted: 1,
                rmjStatus: 2,
                rmjActive: 1,
                vehicleTypeValue: "",
                containerTypeValue: "",
                completedValue: "",
            });
            setSearchFieldReset(true);
            setCtSearchFieldReset(true);
            setCurrentItem({});
            setMultiRequestTransporterErrors({});
            setButtonClicked(false);
        }
    }, [buttonClicked]);

    const useMultiRequestDynamicStatus = useDynamicValidation(requestMultipleJobValidation, FORM, true);

    const onAddClick = async () => {
        setIsAddLoading(true);

        const multiRequestErrors = validateRequestMultipleJob(multiRequestTransporter, useMultiRequestDynamicStatus);
        if (Object.keys(multiRequestErrors).length === 0) {
            if (multiRequestTransporter?.rmjConsigneeCbm !== "" || multiRequestTransporter?.rmjConsigneeCbm !== null) {
                if (multiRequestTransporter?.rmjMaxCmb < multiRequestTransporter?.rmjConsigneeCbm) {
                    toast.warn("Consignee Cbm should be less than Max Cbm", {
                        autoClose: 5000, containerId: "Main",
                    });
                    setMultiRequestTransporterErrors({
                        ...multiRequestTransporterErrors, rmjConsigneeCbm: "Consignee Cbm should be less than Max Cbm",
                    });
                    setIsAddLoading(false);
                    return;
                } else {
                    setMultiRequestTransporterErrors({
                        ...multiRequestTransporterErrors, rmjConsigneeCbm: "",
                    });
                }
            }

            const vehTypeValue = await getFieldValuesByTableAndId({
                tableName: "masterdata.tms_m_vehicle_type",
                primaryKey: "vht_id",
                fieldNames: "vht_type",
                ids: multiRequestTransporter?.rmjVehicleType,
            });
            const vehicleTypeValue = vehTypeValue[0]?.field1 || "";

            const conTypeValue = await getFieldValuesByTableAndId({
                tableName: "masterdata.tms_m_container_type",
                primaryKey: "cot_id",
                fieldNames: "cot_discription",
                ids: multiRequestTransporter?.rmjContainerType,
            });
            const containerTypeValue = conTypeValue[0]?.field1 || "";

            const consigneeValue = await getFieldValuesByTableAndId({
                tableName: "masterdata.tms_m_stakeholder",
                primaryKey: "stk_id",
                fieldNames: "stk_name",
                ids: multiRequestTransporter?.rmjConsignees,
            });
            const consigneeName = consigneeValue[0]?.field1 || "";

            const yesNoValue = await getTextByValueAndKey("yesno", multiRequestTransporter?.rmjCompleted);

            // const rotStatusValue = await getTextByValueAndKey(
            //   "status",
            //   requestOther.rotStatus,
            // );
            // const activeValue = await getTextByValueAndKey(
            //   "active",
            //   multiRequestTransporter?.rmjActive
            // );

            if (!multiRequestTransporter.rmjId) {
                if (!multiRequestTransporter.id) {
                    setGridData((prevState) => [...prevState, {
                        ...multiRequestTransporter,
                        id: count,
                        vehicleTypeValue,
                        containerTypeValue,
                        rmjConsigneeName: consigneeName,
                        completedValue: yesNoValue, // activeValue: activeValue,
                    },]);

                    dispatch(requestActions.addRequestMultipleJobs({
                        ...multiRequestTransporter,
                        id: count,
                        vehicleTypeValue,
                        containerTypeValue,
                        rmjConsigneeName: consigneeName,
                        completedValue: yesNoValue, // activeValue: activeValue,
                    }));
                    setCount((prevState) => prevState + 1);
                } else {
                    setGridData((prevState) => prevState.map((item) => {
                        if (item.id === multiRequestTransporter.id) {
                            return {
                                ...item, ...multiRequestTransporter,
                                vehicleTypeValue,
                                containerTypeValue,
                                rmjConsigneeName: consigneeName,
                                completedValue: yesNoValue, // activeValue: activeValue,
                            };
                        } else {
                            return item;
                        }
                    }));

                    dispatch(requestActions.updateNonPersistedRequestMultipleJobs({
                        requestMultipleJob: {
                            ...multiRequestTransporter,
                            vehicleTypeValue,
                            containerTypeValue,
                            rmjConsigneeName: consigneeName,
                            completedValue: yesNoValue, // activeValue: activeValue,
                        }, id: parseInt(multiRequestTransporter.id),
                    }));
                }
            } else {
                setGridData((prevState) => prevState.map((item) => {
                    if (item.rmjId === multiRequestTransporter.rmjId) {
                        return {
                            ...item, ...multiRequestTransporter,
                            vehicleTypeValue,
                            containerTypeValue,
                            rmjConsigneeName: consigneeName,
                            completedValue: yesNoValue, // activeValue: activeValue,
                        };
                    } else {
                        return item;
                    }
                }));

                dispatch(requestActions.updatePersistedRequestMultipleJobs({
                    requestMultipleJob: {
                        ...multiRequestTransporter,
                        vehicleTypeValue,
                        containerTypeValue,
                        rmjConsigneeName: consigneeName,
                        completedValue: yesNoValue, // activeValue: activeValue,
                    }, rmjId: parseInt(multiRequestTransporter.rmjId),
                }));
            }
            setButtonClicked(true);
        } else {
            setMultiRequestTransporterErrors(multiRequestErrors);
            document.querySelector(".ui.form").classList.add("error");
        }
        setIsAddLoading(false);
    };

    useImperativeHandle(ref, () => ({
        getData: () => requestTransporter,
        getSingleConsignee: () => singleConsignee,
        validateForm: () => {
            const currentValidationStatus = {...dynamicValidationStatus};
            if (requestTransporter.reqMultiConsignee === 1) {
                if (currentValidationStatus.rcoCbm) {
                    delete currentValidationStatus.rcoCbm;
                }
            }
            return validateRequestTransport({
                ...requestTransporter, ...singleConsignee, multiConsignmentType, vehicleMaxCbm,
            }, currentValidationStatus);
        },
        validateMultiConsignee: () => {
            if (requestTransporter.reqMultiConsignee === 2) {
                if (singleConsignee.rcoCbm === null || singleConsignee.rcoCbm === "") {
                    return "CBM is required";
                } else {
                    if (singleConsignee.rcoCbm === 0) {
                        return "CBM is required";
                    } else {
                        return null;
                    }
                }
            } else {
                return null;
            }
        },
        getRequestCreationType: () => reqType,
        getRequestConfirmationStatus: () => confirmationType,
        getMultiRequestTransporterData: () => (reqType !== 1 ? gridData : []),
    }));

    useEffect(() => {
        if (Object.keys(currentItem).length > 0) {
            setMultiRequestTransporter({
                ...currentItem,
            });
        }
    }, [editMode, currentItem]);

    useEffect(() => {
        if (reqCreateType === 2) {
            const activeGridList = gridData?.filter((item) => item.rmjActive === 1);
            let avgCbm = activeGridList?.reduce((total, item) => {
                return total + item.rmjConsigneeCbm;
            }, 0) / activeGridList?.length;
            if (avgCbm === 0 || avgCbm === null || isNaN(avgCbm)) {
                avgCbm = 0;
            }
            dispatch(requestActions.setRequest({
                reqCbm: avgCbm,
            }));
        }
    }, [gridData, reqCreateType, dispatch]);

    return (<>
            {isLoading || isFormVisibilityLoading ? (<LoadingSpinner/>) : (<form className="ui form">
                    <div className="two fields">
                        <Dropdown
                            clearable={false}
                            required={true}
                            label="Request Creation Type"
                            options={dropdown?.requestTypeOptions}
                            value={reqType}
                            onChange={(e, data) => {
                                setReqType(data.value);
                                dispatch(requestActions.setRequest({
                                    reqCreationType: data.value,
                                }));

                                if (data.value === 1) {
                                    setGridData([]);
                                    dispatch(requestActions.clearRequestMultipleJobs());
                                } else {
                                    setRequestTransporter({
                                        reqItemTypes: 7,
                                        reqManageItems: 2,
                                        reqMultiConsignee: 2,
                                        reqConsignmentType: null,
                                        reqVehicleTypeId: null,
                                        reqContainerType: null,
                                        reqContainerno: "",
                                        reqNumberOfVehicles: null,
                                        reqNumberOfTrailers: null,
                                        reqGrossWeight: null,
                                        reqTotalValye: null,
                                        reqConsignee: null,
                                        reqConsigneeCbm: null,
                                        reqConsigneeQty: null,
                                        reqKAGNo: null,
                                    });

                                    setSingleConsignee({
                                        rcoLocation: null,
                                        rcoConsignee: null,
                                        rcoCbm: null,
                                        rcoCbmUom: null,
                                        rcoQty: null,
                                        rcoQtyUom: null,
                                        rcoActive: 1,
                                    });

                                    dispatch(requestActions.setRequest({
                                        vehicleMaxCbm: 0,
                                    }));
                                }
                            }}
                            useDynamicFields={true}
                            form={FORM}
                            module={MODULES.Transaction}
                            field="req_creation_type"
                        />
                        <Dropdown
                            clearable={false}
                            required={true}
                            label="Confirmation Status"
                            options={dropdown.confirmationTypeOptions}
                            value={confirmationType}
                            onChange={(e, data) => {
                                setConfirmationType(data.value);
                                dispatch(requestActions.setRequest({
                                    reqConfirmationStatus: data.value,
                                }));
                            }}
                            useDynamicFields={true}
                            form={FORM}
                            module={MODULES.Transaction}
                            field="req_confirmation_status"
                        />
                    </div>

                    {reqType === 1 ? (<div className="ui segment">
                            <h4 className="ui dividing header">Transport Information</h4>
                            <div className="field">
                                <div className="two fields ">
                                    <SearchDropdown
                                        readOnly={viewOnlyMode}
                                        useDynamicFields={true}
                                        key={1}
                                        editMode={editMode}
                                        reset={searchFieldReset}
                                        setReset={setSearchFieldReset}
                                        value={requestTransporter.reqVehicleTypeId}
                                        form={FORM}
                                        module={MODULES.Transaction}
                                        field="req_vehicle_type"
                                        threshold={1}
                                        clearable={true}
                                        onClear={() => {
                                            setRequestTransporter((prevState) => ({
                                                ...prevState, reqVehicleTypeId: null,
                                            }));
                                        }}
                                        onResultSelect={(data) => setRequestTransporter((prevState) => ({
                                            ...prevState, reqVehicleTypeId: data,
                                        }))}
                                        api={getOtherRefSearchDropdown}
                                        apiDependency={{
                                            type: "req_vehicle_type",
                                            value: "customer_shipper",
                                            param2: customerId?.toString(),
                                            param4: plantId?.toString(),
                                        }}
                                        fieldInfo={{
                                            tableName: "masterdata.tms_m_vehicle_type",
                                            primaryKey: "vht_id",
                                            fieldName: "vht_type",
                                            id: requestTransporter.reqVehicleTypeId,
                                        }}
                                        required={true}
                                        label="Vehicle Type"
                                        checkCount={true}
                                        error={currentErrors.reqVehicleTypeId}
                                        resetError={() => setCurrentErrors((prevState) => {
                                            const {reqVehicleTypeId: _, ...rest} = prevState;
                                            return rest;
                                        })}
                                    />
                                    <div className=" field">
                                        <Input
                                            type="number"
                                            useDynamicFields={true}
                                            min={0}
                                            form={FORM}
                                            module={MODULES.Transaction}
                                            field="vehicle_max_cbm"
                                            name="maxcbm"
                                            label="Max CBM"
                                            value={vehicleMaxCbm}
                                            readOnly
                                        />
                                    </div>
                                </div>
                                <div className="ui segment">
                                    <div className="two fields">
                                        <Dropdown
                                            required={true}
                                            useDynamicFields={true}
                                            readOnly={viewOnlyMode}
                                            label="Multiple Consignees"
                                            clearable={false}
                                            error={currentErrors.reqMultiConsignee}
                                            form={FORM}
                                            module={MODULES.Transaction}
                                            field="req_multi_consignee"
                                            resetError={() => setCurrentErrors((prevState) => {
                                                const {reqMultiConsignee: _, ...rest} = prevState;
                                                return rest;
                                            })}
                                            placeholder="Yes/No"
                                            name="multiConsignee"
                                            options={dropdown.yesNoOptions}
                                            value={requestTransporter.reqMultiConsignee}
                                            style={{
                                                height: "20px",
                                            }}
                                            onChange={(e, data) => {
                                                dispatch(requestActions.setRequest({
                                                    reqMultiConsignee: data.value,
                                                    singleConsignee: null,
                                                    reqCbm: 0,
                                                    reqQty: 0,
                                                }));
                                                setRequestTransporter((prevState) => ({
                                                    ...prevState, reqMultiConsignee: data.value,
                                                }));
                                                setSingleConsignee((prevState) => ({
                                                    ...prevState, rcoQty: "", rcoCbm: "",
                                                }));
                                            }}
                                        />

                              <SearchDropdown
                                  readOnly={viewOnlyMode}
                                  useDynamicFields={true}
                                  key={1}
                                  disabled={requestTransporter.reqMultiConsignee === 1}
                                  editMode={editMode}
                                  reset={searchFieldReset}
                                  setReset={setSearchFieldReset}
                                  value={singleConsignee?.rcoConsignee}
                                  error={currentErrors.rcoConsignee}
                                  resetError={() =>
                                      setCurrentErrors((prevState) => {
                                          const {rcoConsignee: _, ...rest} = prevState;
                                          return rest;
                                      })
                                  }
                                  form={FORM}
                                  module={MODULES.Transaction}
                                  field="req_singaleconsignee_consignee"
                                  checkCount={true}
                                  clearable={true}
                                  onClear={() => {
                                      setSingleConsignee((prevState) => ({
                                          ...prevState,
                                          rcoConsignee: null,
                                      }));
                                      dispatch(
                                          requestActions.setRequest({
                                              singleConsignee: null,
                                          })
                                      );
                                  }}
                                  onResultSelect={(data) => {
                                      setSingleConsignee((prevState) => ({
                                          ...prevState,
                                          rcoConsignee: data,
                                      }));
                                      dispatch(
                                          requestActions.setRequest({
                                              singleConsignee: data,
                                          })
                                      );
                                  }}
                                  // api={getOtherRefSearchDropdown}
                                  // apiDependency={{
                                  //     type: "stakeholder_type",
                                  //     value: "Consignee",
                                  //     param2: customerId,
                                  // }}
                                  api={getCustomerConsigneeSearchDropdown}
                                  apiDependency={{
                                      param2: customerId?.toString(),
                                  }}
                                  fieldInfo={{
                                      tableName: "masterdata.tms_m_stakeholder",
                                      primaryKey: "stk_id",
                                      fieldName: "stk_name",
                                      id: singleConsignee?.rcoConsignee?.toString(),
                                  }}
                                  label="Consignees"
                              />
                          </div>
                          <div className="two fields">
                              <Input
                                  useDynamicFields={true}
                                  readOnly={viewOnlyMode}
                                  disabled={
                                      requestTransporter.reqMultiConsignee === 1 ||
                                      requestTransporter.reqVehicleTypeId === null ||
                                      requestTransporter.reqVehicleTypeId === "" ||
                                      vehicleMaxCbm === null
                                  }
                                  label="CBM"
                                  error={currentErrors.rcoCbm}
                                  form={FORM}
                                  module={MODULES.Transaction}
                                  field={
                                      requestTransporter.reqMultiConsignee === 2
                                          ? "req_singaleconsignee_cbm_man"
                                          : "req_singaleconsignee_cbm"
                                  }
                                  resetError={() =>
                                      setCurrentErrors((prevState) => {
                                          const { rcoCbm: _, ...rest } = prevState;
                                          return rest;
                                      })
                                  }
                                  type="number"
                                  step="0.01"
                                  min={0}
                                  max={parseFloat(vehicleMaxCbm)}
                                  name="cbm"
                                  value={singleConsignee?.rcoCbm}
                                  onChange={(e) => {
                                      const value = e.target.value;
                                      const regex = /^\d*\.?\d{0,2}$/;;

                                                if (!regex.test(value) && value !== "") {
                                                    setCurrentErrors((prevState) => ({
                                                        ...prevState, rcoCbm: "Allow only two decimal places",
                                                    }));
                                                } else {
                                                    setCurrentErrors((prevState) => {
                                                        const {rcoCbm: _, ...rest} = prevState;
                                                        return rest;
                                                    });
                                                }

                                                if (parseFloat(value) > parseFloat(vehicleMaxCbm)) {
                                                    toast.error("CBM should be less than or equal to vehicle max cbm", {
                                                        autoClose: 3000,
                                                        containerId: "Main"
                                                    });
                                                }

                                                setSingleConsignee((prevState) => ({
                                                    ...prevState, rcoCbm: value,
                                                }));

                                                dispatch(requestActions.setRequest({
                                                    reqCbm: value,
                                                }));
                                            }}
                                        />

                                        <Input
                                            label="Qty"
                                            useDynamicFields={true}
                                            readOnly={viewOnlyMode}
                                            error={currentErrors.rcoQty}
                                            form={FORM}
                                            module={MODULES.Transaction}
                                            field="req_singaleconsignee_qty"
                                            resetError={() => setCurrentErrors((prevState) => {
                                                const {rcoQty: _, ...rest} = prevState;
                                                return rest;
                                            })}
                                            disabled={requestTransporter.reqMultiConsignee === 1}
                                            type="number"
                                            min={0}
                                            name="gross_weight"
                                            value={singleConsignee?.rcoQty}
                                            onChange={(e) => {
                                                setSingleConsignee((prevState) => ({
                                                    ...prevState, rcoQty: e.target.value,
                                                }));
                                                dispatch(requestActions.setRequest({
                                                    reqQty: e.target.value,
                                                }));
                                            }}
                                        />
                                    </div>
                                </div>
                                <div className="two fields">
                                    <Dropdown
                                        readOnly={viewOnlyMode}
                                        useDynamicFields={true}
                                        clearable={false}
                                        label="Item Type"
                                        error={currentErrors.reqItemTypes}
                                        form={FORM}
                                        module={MODULES.Transaction}
                                        field="req_item_types"
                                        resetError={() => setCurrentErrors((prevState) => {
                                            const {reqItemTypes: _, ...rest} = prevState;
                                            return rest;
                                        })}
                                        placeholder="Item Type"
                                        options={dropdown.itemTypeOptions}
                                        value={requestTransporter.reqItemTypes}
                                        style={{
                                            height: "20px",
                                        }}
                                        onChange={(e, data) => {
                                            setRequestTransporter((prevState) => ({
                                                ...prevState, reqItemTypes: data.value,
                                            }));
                                        }}
                                    />
                                    <Dropdown
                                        readOnly={viewOnlyMode}
                                        useDynamicFields={true}
                                        required={true}
                                        label="Manage Items"
                                        error={currentErrors.reqManageItems}
                                        form={FORM}
                                        module={MODULES.Transaction}
                                        field="req_manage_items"
                                        resetError={() => setCurrentErrors((prevState) => {
                                            const {reqManageItems: _, ...rest} = prevState;
                                            return rest;
                                        })}
                                        placeholder="Manage Items Yes/No"
                                        options={dropdown.yesNoOptions}
                                        value={requestTransporter.reqManageItems}
                                        style={{
                                            height: "20px",
                                        }}
                                        onChange={(e, data) => {
                                            setRequestTransporter((prevState) => ({
                                                ...prevState, reqManageItems: data.value,
                                            }));
                                        }}
                                    />
                                </div>

                                <div className="two fields ">
                                    <Input
                                        readOnly={viewOnlyMode}
                                        useDynamicFields={true}
                                        label="Container No"
                                        error={currentErrors.reqContainerno}
                                        form={FORM}
                                        module={MODULES.Transaction}
                                        field="req_containerno"
                                        resetError={() => setCurrentErrors((prevState) => {
                                            const {reqContainerno: _, ...rest} = prevState;
                                            return rest;
                                        })}
                                        placeholder="Container No"
                                        value={requestTransporter.reqContainerno}
                                        onChange={(e) => setRequestTransporter((prevState) => ({
                                            ...prevState, reqContainerno: e.target.value,
                                        }))}
                                    />

                                    <SearchDropdown
                                        readOnly={viewOnlyMode}
                                        useDynamicFields={true}
                                        disabled={viewOnlyMode}
                                        key={1}
                                        editMode={editMode}
                                        reset={searchFieldReset}
                                        setReset={setSearchFieldReset}
                                        value={requestTransporter.reqContainerType}
                                        form={FORM}
                                        module={MODULES.Transaction}
                                        field="req_container_type"
                                        error={currentErrors.reqContainerType}
                                        resetError={() => setCurrentErrors((prevState) => {
                                            const {reqContainerType: _, ...rest} = prevState;
                                            return rest;
                                        })}
                                        clearable={true}
                                        checkCount={true}
                                        onClear={() => {
                                            setRequestTransporter((prevState) => ({
                                                ...prevState, reqContainerType: null,
                                            }));
                                        }}
                                        onResultSelect={(data) => setRequestTransporter((prevState) => ({
                                            ...prevState, reqContainerType: data,
                                        }))}
                                        api={getOtherRefSearchDropdown}
                                        apiDependency={{
                                            type: "container_type", value: "",
                                        }}
                                        threshold={1}
                                        fieldInfo={{
                                            tableName: "masterdata.tms_m_container_type",
                                            primaryKey: "cot_id",
                                            fieldName: "cot_discription",
                                            id: requestTransporter.reqContainerType?.toString(),
                                        }}
                                        label="Container Type"
                                    />
                                </div>
                                <div className="two fields ">
                                    <Input
                                        readOnly={viewOnlyMode}
                                        useDynamicFields={true}
                                        label="Number of Trailers"
                                        error={currentErrors.reqNumberOfTrailers}
                                        form={FORM}
                                        module={MODULES.Transaction}
                                        field="req_number_of_trailers"
                                        resetError={() => setCurrentErrors((prevState) => {
                                            const {reqNumberOfTrailers: _, ...rest} = prevState;
                                            return rest;
                                        })}
                                        type="number"
                                        min={0}
                                        name="no_of_trailers"
                                        value={requestTransporter.reqNumberOfTrailers}
                                        onChange={(e) => {
                                            setRequestTransporter((prevState) => ({
                                                ...prevState, reqNumberOfTrailers: e.target.value,
                                            }));
                                        }}
                                    />
                                    <Input
                                        readOnly={viewOnlyMode}
                                        useDynamicFields={true}
                                        label="Number of Vehicles"
                                        error={currentErrors.reqNumberOfVehicles}
                                        form={FORM}
                                        module={MODULES.Transaction}
                                        field="req_number_of_vehicles"
                                        resetError={() => setCurrentErrors((prevState) => {
                                            const {reqNumberOfVehicles: _, ...rest} = prevState;
                                            return rest;
                                        })}
                                        type="number"
                                        min={0}
                                        name="no_of_vehicles"
                                        value={requestTransporter.reqNumberOfVehicles}
                                        onChange={(e) => {
                                            setRequestTransporter((prevState) => ({
                                                ...prevState, reqNumberOfVehicles: e.target.value,
                                            }));
                                        }}
                                    />
                                </div>
                                <div className="two fields ">
                                    <Input
                                        readOnly={viewOnlyMode}
                                        useDynamicFields={true}
                                        label="Gross Weight"
                                        error={currentErrors.reqGrossWeight}
                                        form={FORM}
                                        module={MODULES.Transaction}
                                        field="req_gross_weight"
                                        resetError={() => setCurrentErrors((prevState) => {
                                            const {reqGrossWeight: _, ...rest} = prevState;
                                            return rest;
                                        })}
                                        type="number"
                                        min={0}
                                        name="gross_weight"
                                        value={requestTransporter.reqGrossWeight}
                                        onChange={(e) => {
                                            setRequestTransporter((prevState) => ({
                                                ...prevState, reqGrossWeight: e.target.value,
                                            }));
                                        }}
                                    />
                                    <Input
                                        readOnly={viewOnlyMode}
                                        label="Gross Value"
                                        useDynamicFields={true}
                                        error={currentErrors.reqTotalValye}
                                        form={FORM}
                                        module={MODULES.Transaction}
                                        field="req_total_valye"
                                        resetError={() => setCurrentErrors((prevState) => {
                                            const {reqTotalValye: _, ...rest} = prevState;
                                            return rest;
                                        })}
                                        type="number"
                                        min={0}
                                        name="voy_number"
                                        value={requestTransporter.reqTotalValye}
                                        onChange={(e) => {
                                            setRequestTransporter((prevState) => ({
                                                ...prevState, reqTotalValye: e.target.value,
                                            }));
                                        }}
                                    />
                                </div>
                                <div className="two fields ">
                                    {/*<Dropdown*/}
                                    {/*    readOnly={viewOnlyMode}*/}
                                    {/*    useDynamicFields={true}*/}
                                    {/*    label="Multiple Consignment"*/}
                                    {/*    error={currentErrors.multiConsignmentType}*/}
                                    {/*    form={FORM}*/}
                                    {/*    module={MODULES.Transaction}*/}
                                    {/*    field="req_multiconsignment_multiConsignmentType"*/}
                                    {/*    resetError={() =>*/}
                                    {/*        setCurrentErrors((prevState) => {*/}
                                    {/*            const {multiConsignmentType: _, ...rest} =*/}
                                    {/*                prevState;*/}
                                    {/*            return rest;*/}
                                    {/*        })*/}
                                    {/*    }*/}
                                    {/*    placeholder="Multiple Consignment Yes/No"*/}
                                    {/*    options={dropdown.yesNoOptions}*/}
                                    {/*    value={multiConsignmentType}*/}
                                    {/*    style={{height: "20px"}}*/}
                                    {/*    onChange={(e, data) => {*/}
                                    {/*        setMultiConsignmentType(data.value);*/}
                                    {/*    }}*/}
                                    {/*/>*/}
                                    <Dropdown
                                        readOnly={viewOnlyMode}
                                        useDynamicFields={true}
                                        label="Consignment Types"
                                        error={currentErrors.reqConsignmentType}
                                        form={FORM}
                                        module={MODULES.Transaction}
                                        field="req_consignment_type"
                                        resetError={() => setCurrentErrors((prevState) => {
                                            const {reqConsignmentType: _, ...rest} = prevState;
                                            return rest;
                                        })}
                                        placeholder="Consignment Types"
                                        options={dropdown.consignmentTypeOptions}
                                        value={requestTransporter?.reqConsignmentType}
                                        style={{height: "20px"}}
                                        onChange={(e, data) => {
                                            setRequestTransporter((prevState) => ({
                                                ...prevState, reqConsignmentType: data.value,
                                            }));
                                        }}
                                    />
                                    <Dropdown
                                        readOnly={viewOnlyMode}
                                        useDynamicFields={true}
                                        clearable={true}
                                        label="KAG Number"
                                        error={currentErrors.reqKAGNo}
                                        form={FORM}
                                        module={MODULES.Transaction}
                                        field="req_kag_number"
                                        resetError={() => setCurrentErrors((prevState) => {
                                            const {reqKAGNo: _, ...rest} = prevState;
                                            return rest;
                                        })}
                                        placeholder="Select KAG Number"
                                        options={dropdown.kagTypes}
                                        value={requestTransporter?.reqKAGNo}
                                        style={{height: "20px"}}
                                        onChange={(e, data) => {
                                            setRequestTransporter((prevState) => ({
                                                ...prevState,
                                                reqKAGNo: data.value,
                                            }));
                                        }}
                                    />
                                </div>
                            </div>
                        </div>) : (<>
                            {multiRequestViewPermission ? (<div className="ui segment">
                                    <h4 className="ui dividing header">
                                        Transport Information
                                    </h4>

                                    <div className="two fields ">
                                        <SearchDropdown
                                            readOnly={viewOnlyMode}
                                            // required={true}
                                            key={1}
                                            editMode={editMode}
                                            reset={searchFieldReset}
                                            setReset={setSearchFieldReset}
                                            value={multiRequestTransporter.rmjVehicleType}
                                            useDynamicFields={true}
                                            form={FORM}
                                            module={MODULES.Transaction}
                                            field="rmj_vehicle_type"
                                            threshold={1}
                                            clearable={true}
                                            onClear={() => {
                                                setMultiRequestTransporter((prevState) => ({
                                                    ...prevState, rmjVehicleType: null,
                                                }));
                                            }}
                                            onResultSelect={(data) => setMultiRequestTransporter((prevState) => ({
                                                ...prevState, rmjVehicleType: data,
                                            }))}
                                            api={getOtherRefSearchDropdown}
                                            apiDependency={{
                                                type: "req_vehicle_type",
                                                value: "customer_shipper",
                                                param2: customerId?.toString(),
                                                param4: plantId?.toString(),
                                            }}
                                            fieldInfo={{
                                                tableName: "masterdata.tms_m_vehicle_type",
                                                primaryKey: "vht_id",
                                                fieldName: "vht_type",
                                                id: multiRequestTransporter.rmjVehicleType,
                                            }}
                                            label="Vehicle Type"
                                            checkCount={true}
                                            error={multiRequestTransporterErrors.rmjVehicleType}
                                            resetError={() => setMultiRequestTransporterErrors((prevState) => {
                                                const {rmjVehicleType: _, ...rest} = prevState;
                                                return rest;
                                            })}
                                            multiGrid={true}
                                            multiGridCurrentValue={multiRequestTransporter?.vehicleTypeValue}
                                        />
                                        <Input
                                            type="number"
                                            useDynamicFields={true}
                                            min={0}
                                            form={FORM}
                                            module={MODULES.Transaction}
                                            field="rmj_max_cmb"
                                            name="maxcbm"
                                            label="Max CBM"
                                            value={multiRequestTransporter?.rmjMaxCmb}
                                            readOnly={true}
                                            error={multiRequestTransporterErrors.rmjMaxCmb}
                                            resetError={() => setMultiRequestTransporterErrors((prevState) => {
                                                const {rmjMaxCmb: _, ...rest} = prevState;
                                                return rest;
                                            })}
                                        />
                                    </div>
                                    <div className="two fields ">
                                        <Input
                                            label="Qty"
                                            useDynamicFields={true}
                                            readOnly={viewOnlyMode}
                                            editMode={editMode}
                                            error={multiRequestTransporterErrors.rmjVehicleQty}
                                            form={FORM}
                                            module={MODULES.Transaction}
                                            field="rmj_vehicle_qty"
                                            resetError={() => setMultiRequestTransporterErrors((prevState) => {
                                                const {rmjVehicleQty: _, ...rest} = prevState;
                                                return rest;
                                            })}
                                            // disabled={requestTransporter.reqMultiConsignee === 1}
                                            type="number"
                                            min={0}
                                            name="gross_weight"
                                            value={multiRequestTransporter.rmjVehicleQty}
                                            onChange={(e) => {
                                                setMultiRequestTransporter((prevState) => ({
                                                    ...prevState, rmjVehicleQty: Number(e.target.value),
                                                }));
                                                // dispatch(
                                                //   requestActions.setRequest({
                                                //     reqQty: e.target.value,
                                                //   })
                                                // );

                                                if (Number(e.target.value) !== 1) {
                                                    setIsContainerDisabled(true);
                                                    setMultiRequestTransporter((prevState) => ({
                                                        ...prevState, rmjContianerNumber: "", rmjContainerType: null,
                                                    }));
                                                    setCtSearchFieldReset(true);
                                                } else {
                                                    setIsContainerDisabled(false);
                                                }
                                            }}
                                        />

                      <SearchDropdown
                        readOnly={viewOnlyMode}
                        useDynamicFields={true}
                        key={1}
                        // disabled={requestTransporter.reqMultiConsignee === 1}
                        editMode={editMode}
                        reset={searchFieldReset}
                        setReset={setSearchFieldReset}
                        value={multiRequestTransporter.rmjConsignees}
                        error={multiRequestTransporterErrors.rmjConsignees}
                        resetError={() =>
                          setMultiRequestTransporterErrors((prevState) => {
                            const { rmjConsignees: _, ...rest } = prevState;
                            return rest;
                          })
                        }
                        form={FORM}
                        module={MODULES.Transaction}
                        field="rmj_consignees"
                        checkCount={true}
                        clearable={true}
                        onClear={() => {
                          setMultiRequestTransporter((prevState) => ({
                            ...prevState,
                            rmjConsignees: null,
                          }));
                        }}
                        onResultSelect={(data) => {
                          setMultiRequestTransporter((prevState) => ({
                            ...prevState,
                            rmjConsignees: data,
                          }));
                        }}
                        // api={getOtherRefSearchDropdown}
                        // apiDependency={{
                        //   type: "stakeholder_type",
                        //   value: "Consignee",
                        //   param2: customerId,
                        // }}
                        api={getCustomerConsigneeSearchDropdown}
                        apiDependency={{
                            param2: customerId?.toString(),
                        }}
                        disabled={!customerId}
                        fieldInfo={{
                          tableName: "masterdata.tms_m_stakeholder",
                          primaryKey: "stk_id",
                          fieldName: "stk_name",
                          id: multiRequestTransporter?.rmjConsignees?.toString(),
                        }}
                        label="Consignee"
                        multiGrid={true}
                        multiGridCurrentValue={
                          multiRequestTransporter?.rmjConsigneeName
                        }
                      />
                    </div>
                    <div className="two fields ">
                      <Input
                        //required={requestTransporter.reqMultiConsignee === 2}
                        useDynamicFields={true}
                        editMode={editMode}
                        readOnly={viewOnlyMode}
                        // disabled={
                        //   requestTransporter.reqMultiConsignee === 1 ||
                        //   requestTransporter.reqVehicleTypeId === null ||
                        //   requestTransporter.reqVehicleTypeId === "" ||
                        //   vehicleMaxCbm === null
                        // }
                        label="Consignee CBM"
                        form={FORM}
                        module={MODULES.Transaction}
                        field={"rmj_consignee_cbm"}
                        error={multiRequestTransporterErrors?.rmjConsigneeCbm}
                        resetError={() =>
                          setMultiRequestTransporterErrors((prevState) => {
                            const { rmjConsigneeCbm: _, ...rest } = prevState;
                            return rest;
                          })
                        }
                        type="number"
                        step="0.01"
                        min={0}
                        max={parseFloat(multiRequestTransporter?.rmjMaxCmb)}
                        name="cbm"
                        value={multiRequestTransporter?.rmjConsigneeCbm}
                        onChange={(e) => {
                          const input = e.target;
                          let value = input.value;
                          const regex = /^\d*\.?\d{0,2}$/;

                                                if (!regex.test(value)) {
                                                    value = input.dataset.prevValue || "";
                                                    setMultiRequestTransporterErrors((prevState) => ({
                                                        ...prevState, rmjConsigneeCbm: "Allow only two decimal places",
                                                    }));
                                                    toast.error("Allow only two decimal places", {
                                                        autoClose: 3000, containerId: "Main",
                                                    });
                                                    return;
                                                } else {
                                                    input.dataset.prevValue = value;

                                                    setMultiRequestTransporterErrors((prevState) => {
                                                        const {rmjConsigneeCbm: _, ...rest} = prevState;
                                                        return rest;
                                                    });
                                                }

                                                if (parseFloat(value) > parseFloat(multiRequestTransporter?.rmjMaxCmb)) {
                                                    setMultiRequestTransporterErrors((prevState) => ({
                                                        ...prevState,
                                                        rmjConsigneeCbm: "CBM should be less than or equal to vehicle max cbm",
                                                    }));
                                                    toast.error("CBM should be less than or equal to vehicle max cbm", {
                                                        autoClose: 3000,
                                                        containerId: "Main"
                                                    });
                                                    return;
                                                }

                                                setMultiRequestTransporter((prevState) => ({
                                                    ...prevState, rmjConsigneeCbm: value !== "" ? Number(value) : null,
                                                }));
                                            }}
                                        />
                                        <SearchDropdown
                                            readOnly={viewOnlyMode}
                                            useDynamicFields={true}
                                            disabled={isContainerDisabled}
                                            key={1}
                                            editMode={editMode}
                                            reset={ctSearchFieldReset}
                                            setReset={setCtSearchFieldReset}
                                            value={multiRequestTransporter?.rmjContainerType}
                                            form={FORM}
                                            module={MODULES.Transaction}
                                            field="rmj_container_type"
                                            error={multiRequestTransporterErrors?.rmjContainerType}
                                            resetError={() => setMultiRequestTransporterErrors((prevState) => {
                                                const {rmjContainerType: _, ...rest} = prevState;
                                                return rest;
                                            })}
                                            clearable={true}
                                            checkCount={true}
                                            onClear={() => {
                                                setMultiRequestTransporter((prevState) => ({
                                                    ...prevState, rmjContainerType: null,
                                                }));
                                            }}
                                            onResultSelect={(data) => setMultiRequestTransporter((prevState) => ({
                                                ...prevState, rmjContainerType: data,
                                            }))}
                                            api={getOtherRefSearchDropdown}
                                            apiDependency={{
                                                type: "container_type", value: "",
                                            }}
                                            threshold={1}
                                            fieldInfo={{
                                                tableName: "masterdata.tms_m_container_type",
                                                primaryKey: "cot_id",
                                                fieldName: "cot_discription",
                                                id: multiRequestTransporter?.rmjContainerType?.toString(),
                                            }}
                                            label="Container Type"
                                            multiGrid={true}
                                            multiGridCurrentValue={multiRequestTransporter?.containerTypeValue}
                                        />
                                    </div>
                                    <div className="two fields">
                                        <Input
                                            readOnly={viewOnlyMode}
                                            disabled={isContainerDisabled}
                                            useDynamicFields={true}
                                            label="Container No"
                                            error={multiRequestTransporterErrors?.rmjContianerNumber}
                                            form={FORM}
                                            module={MODULES.Transaction}
                                            field="rmj_contianer_number"
                                            resetError={() => setMultiRequestTransporterErrors((prevState) => {
                                                const {rmjContianerNumber: _, ...rest} = prevState;
                                                return rest;
                                            })}
                                            placeholder="Container No"
                                            value={multiRequestTransporter?.rmjContianerNumber}
                                            onChange={(e) => setMultiRequestTransporter((prevState) => ({
                                                ...prevState, rmjContianerNumber: e.target.value,
                                            }))}
                                            editMode={editMode}
                                        />
                                        <Dropdown
                                            readOnly={viewOnlyMode}
                                            options={dropdown.yesNoOptions}
                                            clearable={false}
                                            label={"Completed"}
                                            value={multiRequestTransporter?.rmjCompleted}
                                            onChange={(e, data) => {
                                                setMultiRequestTransporter((prevState) => ({
                                                    ...prevState, rmjCompleted: data.value,
                                                }));
                                            }}
                                            error={multiRequestTransporterErrors?.rmjCompleted}
                                            editMode={editMode}
                                            useDynamicFields={true}
                                            form={FORM}
                                            module={MODULES.Transaction}
                                            field="rmj_completed"
                                            resetError={() => setMultiRequestTransporterErrors((prevState) => {
                                                const {rmjCompleted: _, ...rest} = prevState;
                                                return rest;
                                            })}
                                        />
                                    </div>
                                    <div
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            width: "100%",
                                            justifyContent: "flex-end",
                                        }}
                                    >
                                        <Button
                                            template="reset"
                                            onClick={(e) => {
                                                setButtonClicked(true);
                                            }}
                                            disabled={viewOnlyMode}
                                        />
                                        {multiRequestCreatePermission ? (<Button
                                                template="addOrSave"
                                                editMode={editMode}
                                                disabled={viewOnlyMode}
                                                create={Object.keys(currentItem).length === 0}
                                                onClick={onAddClick}
                                                isLoading={isAddLoading}
                                            />) : ("")}
                                    </div>
                                    <Grid
                                        searchable={false}
                                        // heading="Other Information List"
                                        config={[{
                                            headerName: "Vehicle Type",
                                            field: "vehicleTypeValue",
                                            cellRenderer: ResizableScrollingTextRenderer,
                                        }, {
                                            headerName: "Max CBM", field: "rmjMaxCmb",
                                        }, {
                                            headerName: "Qty", field: "rmjVehicleQty",
                                        }, {headerName: "Consignee", field: "rmjConsigneeName"}, {
                                            headerName: "Con CBM",
                                            field: "rmjConsigneeCbm"
                                        }, {
                                            headerName: "Container Type", field: "containerTypeValue",
                                        }, {
                                            headerName: "Container No", field: "rmjContianerNumber",
                                        }, {
                                            headerName: "Completed", field: "completedValue",
                                        }, // {
                                            //   headerName: "Active",
                                            //   field: "activeValue",
                                            //   cellRenderer: ActiveCellStyle,
                                            //   pinned: "right",
                                            // },
                                            {
                                                field: "Edit",
                                                filter: true,
                                                cellRenderer: ButtonRenderer,
                                                cellRendererParams: (params) => ({
                                                    // consider: AUTH_TYPES.CREATE,
                                                    clickHandler: () => {
                                                        setCurrentItem(params.data);
                                                    },
                                                    label: viewOnlyMode ? "View" : "Edit",
                                                    disabled: viewOnlyMode || params.data.rmjActive === 2 || !multiRequestEditPermission,
                                                }),
                                            }, {
                                                field: "Delete",
                                                filter: true,
                                                cellRenderer: ButtonRenderer,
                                                cellRendererParams: (params) => ({
                                                    // consider: AUTH_TYPES.DELETE,
                                                    clickHandler: async () => {
                                                        // const activeValue = await getTextByValueAndKey(
                                                        //   "active",
                                                        //   2
                                                        // );
                                                        if (params.data.rmjId) {
                                                            setGridData((prevState) => prevState.map((item) => {
                                                                if (item.rmjId === params.data.rmjId) {
                                                                    return {
                                                                        ...item,
                                                                        rmjActive: 2, // activeValue: activeValue,
                                                                    };
                                                                } else {
                                                                    return item;
                                                                }
                                                            }));

                                                            dispatch(requestActions.removePersistedRequestMultipleJobs({
                                                                rmjId: params.data.rmjId,
                                                            }));
                                                        } else {
                                                            setGridData((prevState) => prevState.map((item) => {
                                                                if (item.id === params.data.id) {
                                                                    return {
                                                                        ...item,
                                                                        rmjActive: 2, // activeValue: activeValue,
                                                                    };
                                                                } else {
                                                                    return item;
                                                                }
                                                            }));

                                                            dispatch(requestActions.removeNonPersistedRequestMultipleJobs({
                                                                id: params.data.id,
                                                            }));
                                                        }
                                                    },
                                                    label: "Delete",
                                                    className: "ui red button",
                                                    disabled: viewOnlyMode || params.data.rmjActive === 2 || !multiRequestDeletePermission,
                                                }),
                                            },]}
                                        content={filteredGridData}
                                    />
                                </div>) : ("")}
                        </>)}
                </form>)}
        </>);
});

export default RequestTransporter;
